import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, FileText, ArrowRight, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useParseJobs } from '@/hooks/useParseJobs';
import { jobState, statusPill, importedLabel, readCount, metaLine } from '@/lib/importStatus';

function relTime(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

function StatusPill({ job }) {
  // Vocabulary lives in lib/importStatus.js — the commit RPC's terminal
  // status for a PDF is `committed`, which this pill used to render as a
  // raw grey chip with a loader icon (looked stuck, forever).
  const m = statusPill(job);
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] ${m.cls}`}>
      <Icon className={`h-3 w-3 ${m.spin ? 'animate-spin' : ''}`} /> {m.label}
    </span>
  );
}

export default function NotificationBell() {
  const { jobs, unseenDone, markSeen, deleteJob } = useParseJobs();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // Status vocabulary is shared with the /import panel — see
  // lib/importStatus.js. `held` = parsed and waiting for the user's review;
  // `imported` = the commit RPC wrote it to the ledger (status `committed`
  // for PDFs, `completed` for CSVs).
  const activeCount = jobs.filter((j) => jobState(j) === 'active').length;
  const badge = unseenDone + activeCount;

  // Auto-acknowledge: as soon as the user OPENS the bell, mark every "done
  // but not-yet-seen" job as seen so the badge clears. The user has now
  // visually acknowledged the notification, keeping the badge red after
  // they've looked feels nagging. Active (queued/processing) jobs still
  // contribute to the badge until they finish.
  useEffect(() => {
    if (!open) return;
    const unseen = jobs.filter((j) => jobState(j) !== 'active' && !j.seen_at);
    if (unseen.length === 0) return;
    // Fire in parallel; don't await, UI shouldn't block.
    unseen.forEach((j) => { markSeen(j.id).catch(() => {}); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleOpenJob = async (job) => {
    await markSeen(job.id);
    setOpen(false);
    // Navigate to import page with the job pre-loaded
    navigate(`/import?job=${job.id}`);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative rounded-xl"
          data-testid="notifications-bell"
          aria-label={`Notifications${badge ? ` (${badge})` : ''}`}
        >
          <Bell className="h-4 w-4" />
          {badge > 0 && (
            <span
              className="absolute top-1 right-1 min-w-[16px] h-[16px] px-1 grid place-items-center rounded-full bg-[hsl(var(--gold))] text-[9px] font-semibold text-foreground"
              data-testid="notifications-badge"
            >
              {badge > 9 ? '9+' : badge}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0 rounded-2xl" data-testid="notifications-panel">
        <div className="px-4 py-3 border-b border-border/60 flex items-center justify-between">
          <div>
            <div className="font-display text-sm font-semibold">Statement imports</div>
            <div className="text-[11px] text-muted-foreground">
              {activeCount > 0
                ? `${activeCount} processing…`
                : jobs.length === 0
                  ? 'No imports yet'
                  : 'Recent imports'}
            </div>
          </div>
        </div>
        {/* Phase 84.18 — Radix's ScrollArea with just a max-height on the root
            doesn't reliably render its scrollbar inside a Popover on all
            browsers (the Viewport uses display:table which fights the
            popover's own height calc). A plain overflow-y-auto div is
            simpler, works consistently, and shows a native scrollbar when
            the list is long enough to overflow. */}
        <div
          className="max-h-[60vh] sm:max-h-[420px] overflow-y-auto overscroll-contain"
          data-testid="notifications-scroll"
        >
          {jobs.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-muted-foreground" data-testid="notifications-empty">
              When you import a statement, you&rsquo;ll see its progress here.
              <div className="mt-3">
                <Link to="/import"><Button size="sm" className="rounded-xl">Import a statement</Button></Link>
              </div>
            </div>
          ) : (
            <ul className="divide-y divide-border/60">
              {jobs.map((job) => {
                const state = jobState(job);
                const isHeld = state === 'held';
                const isFailed = state === 'failed';
                const isActive = state === 'active';
                const isImported = state === 'imported' || state === 'purged';
                return (
                  <li key={job.id} className="px-4 py-3 hover:bg-accent/30 transition-colors" data-testid={`notification-row-${job.id}`} data-row-state={state}>
                    <div className="flex items-start gap-2.5">
                      <FileText className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="text-sm font-medium truncate max-w-[180px]" title={job.filename}>{job.filename}</div>
                          <StatusPill job={job} />
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          {isImported && metaLine(importedLabel(job), relTime(job.consumed_at || job.finished_at))}
                          {isHeld && metaLine(`Read ${readCount(job)} transaction${readCount(job) === 1 ? '' : 's'}`, relTime(job.finished_at))}
                          {isFailed && (
                            <span className="text-destructive break-words" title={job.error}>
                              {job.error || 'Couldn’t read this statement — it may be scanned or password-protected'}
                            </span>
                          )}
                          {isActive && `Started ${relTime(job.started_at || job.created_at)}`}
                        </div>
                        {isHeld && (
                          <div className="mt-2 flex items-center gap-2">
                            <Button
                              size="sm"
                              className="rounded-xl h-7 px-2.5"
                              onClick={() => handleOpenJob(job)}
                              data-testid={`notification-open-${job.id}`}
                            >
                              Review &amp; import <ArrowRight className="h-3 w-3 ml-1" />
                            </Button>
                          </div>
                        )}
                        {isImported && (
                          <div className="mt-2 flex items-center gap-2">
                            <Link
                              to="/transactions"
                              onClick={() => setOpen(false)}
                              data-testid={`notification-view-${job.id}`}
                            >
                              <Button
                                size="sm"
                                variant="ghost"
                                className="rounded-xl h-7 px-2.5 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-500/10"
                              >
                                View in transactions <ArrowRight className="h-3 w-3 ml-1" />
                              </Button>
                            </Link>
                          </div>
                        )}
                      </div>
                      {!isActive && (
                        <button
                          onClick={() => deleteJob(job.id)}
                          className="text-muted-foreground/60 hover:text-foreground p-1 rounded-md"
                          aria-label="Dismiss"
                          data-testid={`notification-dismiss-${job.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
